import { createContext, useContext, useState, useEffect } from 'react'
import Client from 'shopify-buy'

const SHOPIFY_ACCESS_TOKEN = process.env.SHOPIFY_ACCESS_TOKEN || ''
const SHOPIFY_DOMAIN = process.env.SHOPIFY_DOMAIN || ''

const client = Client.buildClient({
  domain: SHOPIFY_DOMAIN,
  storefrontAccessToken: SHOPIFY_ACCESS_TOKEN,
})

type ShoppingContextType = {
  client: Client.Client
  checkout?: Client.Cart
  setCheckout: (checkout: Client.Cart) => void
}

const ShoppingContext = createContext({} as ShoppingContextType)

type Props = {
  children: React.ReactNode
}

export function ShoppingProvider({ children }: Props) {
  const [checkout, setCheckout] = useState<Client.Cart>()

  useEffect(() => {
    client.checkout.create().then(setCheckout)
  }, [])

  return (
    <ShoppingContext.Provider value={{ client, checkout, setCheckout }}>
      {children}
    </ShoppingContext.Provider>
  )
}

export function useProduct(id: string) {
  const { client } = useContext(ShoppingContext)
  const [product, setProduct] = useState<Client.Product>()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    client.product.fetch(id).then((product) => {
      setProduct(product)
      setIsLoading(false)
    })
  }, [id])

  return {
    isLoading,
    product,
  }
}

export function useCheckout() {
  const { client, checkout, setCheckout } = useContext(ShoppingContext)

  const onAddToCart = (lineItemsToAdd: Client.LineItemToAdd[]) => {
    if (client.checkout && checkout) {
      client.checkout
        .addLineItems(checkout.id, lineItemsToAdd)
        .then(setCheckout)
    }
  }

  function handleUpdateCheckout(attributes: ShopifyBuy.CustomAttribute[]) {
    if (checkout) {
      client.checkout
        .updateAttributes(checkout.id, { customAttributes: attributes })
        .then(setCheckout)
    }
  }

  return {
    onAddToCart,
    onUpdateCheckout: handleUpdateCheckout,
    amount: checkout && checkout.totalPriceV2.amount,
    checkoutUrl: checkout && checkout.webUrl,
    checkout: checkout,
  }
}
